import { createSignal, createEffect } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import { useAuthenticatedUser } from '../contexts/AuthContext';
import { apiConfig } from '../utils/apiConfig';

export interface SessionItem {
	id: string;
	type: 'game' | 'queue';
	displayName: string;
	fullId: string;
}

export const useSessionRecovery = () => {
	const { user, setUser } = useAuthenticatedUser();
	const navigate = useNavigate();
	const [validatingSession, setValidatingSession] = createSignal<string | null>(null);

	const sessions = (): SessionItem[] => {
		const items: SessionItem[] = [];
		const currentUser = user();

		if (currentUser) {
			// Add active games with validation
			currentUser.activeGameIds.forEach((gameId) => {
				if (gameId && gameId.trim().length > 0) {
					const safeName =
						gameId.length >= 6 ? `GAME-${gameId.slice(-6).toUpperCase()}` : `GAME-${gameId.toUpperCase()}`;

					items.push({
						id: gameId,
						type: 'game',
						displayName: safeName,
						fullId: gameId,
					});
				} else {
					console.warn('⚠️ [SessionRecovery] Invalid game ID found:', gameId);
				}
			});

			// Add matchmaking queue with validation
			if (currentUser.matchmakingQueueId && currentUser.matchmakingQueueId.trim().length > 0) {
				const queueId = currentUser.matchmakingQueueId;
				const safeName =
					queueId.length >= 6 ? `QUEUE-${queueId.slice(-6).toUpperCase()}` : `QUEUE-${queueId.toUpperCase()}`;

				items.push({
					id: queueId,
					type: 'queue',
					displayName: safeName,
					fullId: queueId,
				});
			} else if (currentUser.matchmakingQueueId) {
				console.warn('⚠️ [SessionRecovery] Invalid queue ID found:', currentUser.matchmakingQueueId);
			}
		}

		return items;
	};

	const validateGameExists = async (gameId: string): Promise<boolean> => {
		try {
			if (!gameId || gameId.trim().length === 0) {
				console.warn('⚠️ [SessionRecovery] Invalid game ID for validation:', gameId);
				return false;
			}

			const response = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.game}/${gameId}/ws`, {
				method: 'HEAD',
				credentials: 'include',
			});

			console.log('🔍 [SessionRecovery] Game validation result:', {
				gameId: gameId.slice(-6),
				status: response.status,
				ok: response.ok,
			});

			return response.ok;
		} catch (error) {
			console.error('❌ [SessionRecovery] Game validation failed:', error);
			return false;
		}
	};

	const removeInvalidSession = (sessionId: string, type: 'game' | 'queue') => {
		const currentUser = user();
		if (!currentUser) return;

		console.log('🧹 [SessionRecovery] Removing invalid session:', { sessionId, type });

		if (type === 'game') {
			const updatedGameIds = currentUser.activeGameIds.filter((id) => id !== sessionId);
			setUser({
				...currentUser,
				activeGameIds: updatedGameIds,
			});
		} else if (type === 'queue') {
			setUser({
				...currentUser,
				matchmakingQueueId: null,
			});
		}
	};

	const cleanupInvalidSessions = () => {
		const currentUser = user();
		if (!currentUser) return;

		let hasInvalidSessions = false;
		const validGameIds: string[] = [];

		// Check for invalid game IDs
		currentUser.activeGameIds.forEach((gameId) => {
			if (gameId && gameId.trim().length > 0) {
				validGameIds.push(gameId);
			} else {
				console.warn('🧹 [SessionRecovery] Removing invalid game ID:', gameId);
				hasInvalidSessions = true;
			}
		});

		// Check for invalid queue ID
		let validQueueId = currentUser.matchmakingQueueId;
		if (currentUser.matchmakingQueueId && currentUser.matchmakingQueueId.trim().length === 0) {
			console.warn('🧹 [SessionRecovery] Removing invalid queue ID:', currentUser.matchmakingQueueId);
			validQueueId = null;
			hasInvalidSessions = true;
		}

		// Update user data if we found invalid sessions
		if (hasInvalidSessions) {
			setUser({
				...currentUser,
				activeGameIds: validGameIds,
				matchmakingQueueId: validQueueId,
			});
		}
	};

	const joinSession = async (session: SessionItem) => {
		setValidatingSession(session.id);

		console.log('🚀 [SessionRecovery] Joining session:', {
			id: session.id.slice(-6),
			type: session.type,
			displayName: session.displayName,
		});

		try {
			if (session.type === 'game') {
				const gameExists = await validateGameExists(session.id);
				if (gameExists) {
					console.log('✅ [SessionRecovery] Game validated, navigating to game');
					navigate(`/game/${session.id}`);
				} else {
					console.warn('⚠️ [SessionRecovery] Game no longer exists, removing from active sessions');
					removeInvalidSession(session.id, 'game');
				}
			} else if (session.type === 'queue') {
				console.log('✅ [SessionRecovery] Navigating to lobby for matchmaking queue');
				navigate('/lobby');
			}
		} catch (error) {
			console.error('❌ [SessionRecovery] Error joining session:', error);
			removeInvalidSession(session.id, session.type);
		} finally {
			setValidatingSession(null);
		}
	};

	const exitSession = async (session: SessionItem) => {
		setValidatingSession(session.id);

		try {
			if (session.type === 'game') {
				const response = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.game}/${session.id}/player`, {
					method: 'DELETE',
					credentials: 'include',
				});

				if (response.ok) {
					removeInvalidSession(session.id, 'game');
				} else {
					console.error('Failed to exit game');
				}
			} else if (session.type === 'queue') {
				const response = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.matchmaking}/rooms/${session.id}`, {
					method: 'DELETE',
					credentials: 'include',
				});

				if (response.ok) {
					removeInvalidSession(session.id, 'queue');
				} else {
					console.error('Failed to leave queue');
				}
			}
		} catch (error) {
			console.error('Error exiting session:', error);
		} finally {
			setValidatingSession(null);
		}
	};

	// Clean up invalid sessions on mount and when user data changes
	createEffect(() => {
		const currentUser = user();
		if (currentUser) {
			cleanupInvalidSessions();
		}
	});

	return {
		sessions,
		validatingSession,
		joinSession,
		exitSession,
		cleanupInvalidSessions,
	};
};
